// ─────────────────────────────────────────────────────────────────
// RichTextToolbar.tsx
// ─────────────────────────────────────────────────────────────────
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Baseline,
  RemoveFormatting,
} from 'lucide-react';

interface Props {
  editor: Editor;
}

export function RichTextToolbar({ editor }: Props) {
  return (
    // `editor-chrome` opts this subtree out of the canvas's document mirror
    // (see globals.css): the toolbar is furniture, so it keeps the editor's own
    // type metrics even when the document's base font size is much larger.
    <div className="editor-chrome border-line bg-card mb-1 flex w-fit items-center gap-0.5 rounded-xl border p-1 shadow-sm">
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        <Bold size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        <Italic size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="Underline"
      >
        <Underline size={13} />
      </ToolbarBtn>

      <div className="bg-line mx-1 h-4 w-px" />

      <ToolbarBtn
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="Align left"
      >
        <AlignLeft size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="Align center"
      >
        <AlignCenter size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
        title="Align right"
      >
        <AlignRight size={13} />
      </ToolbarBtn>

      <div className="bg-line mx-1 h-4 w-px" />

      <ColorPicker editor={editor} />
      <ToolbarBtn
        onClick={() => editor.chain().focus().unsetColor().run()}
        title="Clear text color"
      >
        <RemoveFormatting size={13} />
      </ToolbarBtn>

      <div className="bg-line mx-1 h-4 w-px" />

      <ToolbarBtn
        onClick={() => {
          const url = window.prompt('URL:');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        active={editor.isActive('link')}
        title="Add link"
      >
        <Link size={13} />
      </ToolbarBtn>
    </div>
  );
}

function ColorPicker({ editor }: Props) {
  const current = (editor.getAttributes('textStyle').color as string | undefined) ?? '#000000';

  return (
    // The native picker blurs the editor, but ProseMirror keeps the selection
    // in state, so `focus()` restores it before the color is applied.
    <label
      title="Text color"
      className="text-muted hover:bg-card-2 hover:text-ink-soft relative flex h-6 w-6 cursor-pointer flex-col items-center justify-center rounded-md transition-colors"
    >
      <Baseline size={13} />
      <span className="h-[3px] w-3.5 rounded-full" style={{ backgroundColor: current }} />
      <input
        type="color"
        value={current}
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        className="absolute inset-0 h-0 w-0 opacity-0"
      />
    </label>
  );
}

function ToolbarBtn({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
        active
          ? 'bg-accent/12 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
          : 'text-muted hover:bg-card-2 hover:text-ink-soft'
      }`}
    >
      {children}
    </button>
  );
}
